import {
  analyticsConsentChangedEvent,
  getConsentPreferences,
  type ConsentPreferences,
} from "./analytics";

const metaPixelId = "4394833684067278";
const metaFrameReadyMessage = "kwd-meta-frame-ready";
const metaFrameCommandMessage = "kwd-meta-frame-command";

type PaidPlan = "serieus" | "premium" | "gold";

type MetaConversion = {
  eventName: "CompleteRegistration" | "InitiateCheckout";
  parameters: Record<string, string | number>;
};

let listenerInstalled = false;
let metaFrame: HTMLIFrameElement | null = null;
let frameReady = false;
let pixelInitialized = false;
const trackedConversions = new Set<string>();
const pendingConversions: MetaConversion[] = [];

export function initMetaPixelConsent(): void {
  if (typeof window === "undefined" || listenerInstalled) return;
  listenerInstalled = true;

  window.addEventListener(
    analyticsConsentChangedEvent,
    handleConsentChanged as EventListener,
  );
  window.addEventListener("message", handleFrameMessage);

  if (getConsentPreferences()?.marketing) {
    ensureMetaFrame();
  }
}

export function trackMetaCompleteRegistration(): void {
  trackMetaConversion("CompleteRegistration", {}, "registration");
}

export function trackMetaInitiateCheckout(input: {
  value: number;
  plan: PaidPlan;
  attemptId: string;
}): void {
  if (!Number.isFinite(input.value) || input.value <= 0) return;

  trackMetaConversion(
    "InitiateCheckout",
    {
      value: input.value,
      currency: "EUR",
      content_name: input.plan,
    },
    `checkout:${input.attemptId}`,
  );
}

function handleConsentChanged(event: CustomEvent<ConsentPreferences>): void {
  if (event.detail.marketing) {
    ensureMetaFrame();
  } else {
    pendingConversions.length = 0;
    frameReady = false;
    pixelInitialized = false;
    metaFrame?.remove();
    metaFrame = null;
  }
}

function handleFrameMessage(event: MessageEvent): void {
  if (
    !metaFrame ||
    event.origin !== window.location.origin ||
    event.source !== metaFrame.contentWindow ||
    event.data?.type !== metaFrameReadyMessage
  ) {
    return;
  }
  if (!getConsentPreferences()?.marketing) {
    metaFrame.remove();
    metaFrame = null;
    return;
  }

  frameReady = true;
  postFrameCommand({ command: "init", pixelId: metaPixelId });
  pixelInitialized = true;
  flushPendingConversions();
}

function trackMetaConversion(
  eventName: MetaConversion["eventName"],
  parameters: MetaConversion["parameters"],
  dedupeKey: string,
): void {
  if (!getConsentPreferences()?.marketing) return;
  if (trackedConversions.has(dedupeKey)) return;

  trackedConversions.add(dedupeKey);
  pendingConversions.push({ eventName, parameters });
  ensureMetaFrame();
  flushPendingConversions();
}

function ensureMetaFrame(): void {
  if (metaFrame || !getConsentPreferences()?.marketing) return;

  const frame = document.createElement("iframe");
  frame.hidden = true;
  frame.title = "Meta-conversiemeting";
  frame.referrerPolicy = "no-referrer";
  frame.setAttribute("sandbox", "allow-scripts allow-same-origin");
  frame.src = `${import.meta.env.BASE_URL}meta-pixel-frame.html`;
  document.body.appendChild(frame);
  metaFrame = frame;
}

function flushPendingConversions(): void {
  if (
    !frameReady ||
    !pixelInitialized ||
    !getConsentPreferences()?.marketing
  ) {
    return;
  }

  for (const conversion of pendingConversions.splice(0)) {
    postFrameCommand({
      command: "track",
      eventName: conversion.eventName,
      parameters: conversion.parameters,
    });
  }
}

function postFrameCommand(command: Record<string, unknown>): void {
  metaFrame?.contentWindow?.postMessage(
    { type: metaFrameCommandMessage, ...command },
    window.location.origin,
  );
}